/* FSK living-gradient haze drift. External file (not inline) so a storefront CSP
   that forbids inline scripts can't silently kill it, and `defer` guarantees it
   runs after Horizon's .page-wrapper exists. Reads the live scroll every frame
   from whichever element actually scrolls (Horizon scrolls .page-wrapper above
   990px, the document below it). Reduced-motion DAMPENS the drift, not disables.

   MOTION MODEL (HAZE-RECIPE.md §2a, ruled 2026-07-11, implemented 2026-07-19):
   Each layer = a slow one-way GLIDE + a two-frequency SWAY that oscillates back
   and forth across the scroll. The sway is what reads as water: layers are given
   staggered phases and non-harmonic frequencies, so opposing sways pass each
   other instead of moving as one block ("like a boat moving in the water").
   Everything is a function of scroll progress p — zero time-based animation. */
(function () {
  try {
    var haze = document.querySelector('.fsk-haze');
    if (!haze) return;
    var rm = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
    var amp = rm ? 0.35 : 1;  /* gentler under reduced-motion, still alive */
    var wobK = rm ? 0.25 : 1; /* sway + rotation nearly flat under reduced-motion */

    /* Global amplitude dial. CSS may override via --haze-motion on .fsk-haze. */
    var cssMotion = parseFloat(getComputedStyle(haze).getPropertyValue('--haze-motion'));
    var M = isFinite(cssMotion) && cssMotion > 0 ? cssMotion : 1;

    var vw = function (n) { return n * innerWidth / 100; };
    var vh = function (n) { return n * innerHeight / 100; };

    /* tx/ty = one-way glide (vw/vh over the full page). sx/sy = sway amplitude.
       p1/p2 = sway PERIOD in SCREENFULS — i.e. one full sway per p1 viewport
       heights of scrolling. This is deliberately keyed to scroll DISTANCE, not
       to page fraction: fraction-based motion silently slows down as a page
       gets longer, which froze the field on long templates (the bug this
       replaces). Distance-keyed sway moves at the same felt rate on every page.
       ph = phase offset in turns, staggering the layers against each other.
       s  = end scale. r0/r = rotation start/end deg. rw = rotation sway deg. */
    var fogs = [
      { el: '.fog-blue',   tx: -14, ty: 18,  sx: 9.0,  sy: 5.0, p1: 3.1, p2: 5.7, ph: 0.00, s: 1.14, r0: 0,  r: 0,   rw: 1.6, spd: 1.0 },
      { el: '.fog-teal',   tx: 16,  ty: -16, sx: 11.0, sy: 6.5, p1: 2.4, p2: 4.3, ph: 0.37, s: 1.22, r0: 0,  r: 5,   rw: 2.2, spd: 1.25 },
      { el: '.fog-teal2',  tx: -12, ty: -20, sx: 8.0,  sy: 5.5, p1: 3.8, p2: 2.6, ph: 0.63, s: 1.12, r0: 0,  r: 0,   rw: 1.8, spd: 0.85 },
      { el: '.fog-citrus', tx: 20,  ty: -10, sx: 12.0, sy: 4.5, p1: 2.8, p2: 5.1, ph: 0.18, s: 1.26, r0: -7, r: -18, rw: 2.6, spd: 1.4 },
      { el: '.fog-amber',  tx: 10,  ty: 16,  sx: 9.5,  sy: 6.0, p1: 2.1, p2: 3.5, ph: 0.81, s: 0.86, r0: 0,  r: 0,   rw: 2.0, spd: 1.1 }
    ].map(function (f) { f.node = haze.querySelector(f.el); return f; })
     .filter(function (f) { return f.node; });
    if (!fogs.length) return;

    var TAU = Math.PI * 2;

    /* precompute each layer's sway value at u=0 so it can be subtracted out */
    fogs.forEach(function (f) {
      var b1 = TAU * f.ph, b2 = TAU * f.ph * 1.7;
      f.zx1 = Math.sin(b1); f.zx2 = Math.sin(b2);
      f.zy1 = Math.cos(b1); f.zy2 = Math.cos(b2);
      f.zr  = Math.sin(b1 + 0.5);
    });

    /* Cache the .page-wrapper element so scroller() does not querySelector on
       every animation frame (perf). Re-query on resize, or if the cached node
       has been detached (theme-editor section reloads can swap it). The frame
       still reads the real scroller structurally (PF-37); only WHEN it runs
       changed, see the note above frame(). */
    var pwCache = document.querySelector('.page-wrapper');
    /* resize: re-find the scroller and forget the last render, because the transforms are in
       viewport units and a width-only resize changes them without changing p or u. */
    window.addEventListener('resize', function () {
      pwCache = document.querySelector('.page-wrapper');
      lastP = lastU = -1;
      schedule();
    }, { passive: true });

    /* Pick the real scroller STRUCTURALLY (can it scroll?) rather than by
       comparing scrollTop values — comparing positions ties at 0 and can flip
       source mid-scroll if the document also happens to move a little. */
    function scroller() {
      var pw = pwCache;
      if (pw && !pw.isConnected) { pw = pwCache = document.querySelector('.page-wrapper'); }
      if (pw && pw.scrollHeight > pw.clientHeight + 4) {
        return { top: pw.scrollTop, max: pw.scrollHeight - pw.clientHeight };
      }
      var de = document.documentElement;
      var top = window.pageYOffset || de.scrollTop || (document.body ? document.body.scrollTop : 0) || 0;
      var sh = Math.max(de.scrollHeight, document.body ? document.body.scrollHeight : 0);
      return { top: top, max: sh - innerHeight };
    }

    var lastP = -1, lastU = -1;
    /* p = fraction of page scrolled (drives the slow one-way glide + scale)
       u = screenfuls scrolled            (drives the sway — page-length neutral) */
    function render(p, u) {
      for (var i = 0; i < fogs.length; i++) {
        var f = fogs[i];
        var q = Math.min(1, p * f.spd);                       /* glide progress */
        var a1 = TAU * (u / f.p1 + f.ph);
        var a2 = TAU * (u / f.p2 + f.ph * 1.7);
        /* two-frequency sway: the beat between p1 and p2 keeps it from looking
           like a metronome. x uses sin, y uses cos → elliptical rocking.
           Each term is zeroed at u=0 (f.z*) so the top of every page still
           renders the art-directed at-rest composition — the sway grows out of
           it rather than starting mid-swing. */
        var swayX = ((Math.sin(a1) - f.zx1) * 0.72 + (Math.sin(a2) - f.zx2) * 0.28) * wobK * M;
        var swayY = ((Math.cos(a1) - f.zy1) * 0.68 + (Math.cos(a2) - f.zy2) * 0.32) * wobK * M;
        var rot = (f.r0 + (f.r - f.r0) * q) +
                  (Math.sin(a1 + 0.5) - f.zr) * f.rw * wobK * M;
        var sc = 1 + (f.s - 1) * q * amp;

        var x = vw(f.tx) * q * amp + vw(f.sx) * swayX;
        var y = vh(f.ty) * q * amp + vh(f.sy) * swayY;

        f.node.style.transform =
          'translate3d(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px,0)' +
          ' scale(' + sc.toFixed(3) + ')' +
          (rot ? ' rotate(' + rot.toFixed(2) + 'deg)' : '');
      }
    }

    /* 2026-10-07 (speed, N-web-defects): the loop used to run on EVERY frame for the life of
       the page, and each frame read scrollHeight/clientHeight, which forces a layout flush
       while the page is still loading. Profiled on a throttled phone that cost 7.1 s of CPU
       on the Lion's Mane page and 3.0 s on the homepage. The frame now runs only when
       something scrolled or resized. The listener is on DOCUMENT in the CAPTURE phase, which
       receives scroll events from .page-wrapper as well as the document, so the desktop
       trap (a window listener never fires above 990px) does not apply. Nothing scrolled
       means p = u = 0, the at-rest frame render(0, 0) already painted. */
    var queued = false;
    function frame() {
      queued = false;
      var m = scroller();
      var p = Math.min(1, Math.max(0, m.top / Math.max(1, m.max)));
      var u = m.top / Math.max(1, innerHeight);
      if (Math.abs(p - lastP) > 0.0002 || Math.abs(u - lastU) > 0.0008) {
        lastP = p; lastU = u; render(p, u);
      }
    }
    function schedule() { if (!queued) { queued = true; requestAnimationFrame(frame); } }
    document.addEventListener('scroll', schedule, { capture: true, passive: true });
    /* the page keeps growing while it loads; one pass at load re-reads the scroll range */
    window.addEventListener('load', schedule);
    render(0, 0);
    schedule();
  } catch (e) {}
})();
